import { getBlogPostBySlug } from "./blogPosts";

const SITE_URL_FALLBACK = "https://oovault.nl";
const DEFAULT_OG_IMAGE_PATH = "/social-card.png";

type SeoPayload = {
  title: string;
  description: string;
  robots: string;
  canonicalPath?: string;
  ogType?: "website" | "article";
  structuredData?: Record<string, unknown>;
};

const managedMetaNames = [
  "description",
  "robots",
  "og:title",
  "og:description",
  "og:type",
  "og:url",
  "og:image",
  "twitter:card",
  "twitter:title",
  "twitter:description",
  "twitter:image",
];

function normalizeSiteUrl(url: string | undefined): string {
  if (!url) return SITE_URL_FALLBACK;
  return url.replace(/\/+$/, "");
}

function publicSiteUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeSiteUrl(window.location.origin);
  }
  return normalizeSiteUrl(
    (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ??
      (import.meta.env.VITE_APP_URL as string | undefined)
  );
}

function absoluteUrl(path: string): string {
  return `${publicSiteUrl()}${path.startsWith("/") ? path : `/${path}`}`;
}

function upsertMeta(name: string, content: string) {
  const useProperty = name.startsWith("og:");
  const selector = useProperty
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;

  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(useProperty ? "property" : "name", name);
    el.setAttribute("data-seo-managed", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string | undefined) {
  const selector = 'link[rel="canonical"]';
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-seo-managed", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertStructuredData(structuredData: Record<string, unknown> | undefined) {
  const selector = 'script[type="application/ld+json"][data-seo-managed="true"]';
  const existing = document.head.querySelector<HTMLScriptElement>(selector);
  if (!structuredData) {
    existing?.remove();
    return;
  }
  const script = existing ?? document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo-managed", "true");
  script.text = JSON.stringify(structuredData);
  if (!existing) {
    document.head.appendChild(script);
  }
}

function cleanupManagedSeo() {
  document.head
    .querySelectorAll('[data-seo-managed="true"]')
    .forEach(node => node.remove());
}

function getSeoPayload(pathname: string): SeoPayload {
  if (pathname === "/") {
    return {
      title: "Double O - Secure Secrets Management for Data Teams",
      description:
        "Manage API keys and credentials securely with short-lived tokens, IP allowlists, and proxy-based secret injection for vibe-coding and agents.",
      robots: "index,follow",
      canonicalPath: "/",
      ogType: "website",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Double O",
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Web",
        description:
          "Secure secrets management with short-lived tokens, proxy pass, and IP allowlisting.",
        url: absoluteUrl("/"),
      },
    };
  }

  if (pathname === "/docs") {
    return {
      title: "Double O Docs - Quickstart and API Usage",
      description:
        "Learn how to store secrets, generate tokens, configure proxy pass, and load virtual .env variables with Double O.",
      robots: "index,follow",
      canonicalPath: "/docs",
      ogType: "article",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: "Double O Documentation",
        description:
          "Quickstart and usage guide for secure secret access and proxy workflows.",
        url: absoluteUrl("/docs"),
      },
    };
  }

  if (pathname === "/pricing") {
    return {
      title: "Double O Pricing - Free, Pro, and Max Plans",
      description:
        "Compare Double O pricing plans for secure secrets management, tokenized access, and proxy workflows for vibe-coding and agents.",
      robots: "index,follow",
      canonicalPath: "/pricing",
      ogType: "website",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Double O Pricing",
        description:
          "Pricing plans for Double O secure secrets management.",
        url: absoluteUrl("/pricing"),
      },
    };
  }

  if (pathname === "/skills") {
    return {
      title: "Double O Skills - Installable Extensions for Your Workflow",
      description:
        "Browse and install community skills for Double O. Extend your secrets management workflow with one command.",
      robots: "index,follow",
      canonicalPath: "/skills",
      ogType: "website",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Double O Skills",
        description:
          "Installable extensions for Double O secrets management workflows.",
        url: absoluteUrl("/skills"),
      },
    };
  }

  if (pathname === "/blog") {
    return {
      title: "Double O Blog - Secrets Management Guides and Best Practices",
      description:
        "Read practical articles on runtime secret injection, Virtual .env workflows, and IP whitelisting for secure development.",
      robots: "index,follow",
      canonicalPath: "/blog",
      ogType: "website",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Double O Blog",
        description:
          "Guides and best practices for secure secrets management and tokenized access workflows.",
        url: absoluteUrl("/blog"),
        blogPost: [
          {
            "@type": "BlogPosting",
            headline:
              "CLI Re-Authentication: How Double O Keeps Your Scripts Running Without Exposing Secrets",
            datePublished: "2026-03-08",
          },
          {
            "@type": "BlogPosting",
            headline:
              "From .env to Double O: The Next Evolution in Secrets Management",
            datePublished: "2026-02-18",
          },
          {
            "@type": "BlogPosting",
            headline:
              "Hardcoded Secrets vs. Runtime Injection: Why Your API Keys Don't Belong in Your Code",
            datePublished: "2026-01-30",
          },
          {
            "@type": "BlogPosting",
            headline:
              "Beyond the Bouncer: How Effective is IP Whitelisting in 2026?",
            datePublished: "2026-01-12",
          },
        ],
      },
    };
  }

  if (pathname === "/privacy") {
    return {
      title: "Privacy Policy - Double O",
      description:
        "Learn how Double O collects, uses, and protects your personal data. GDPR and CCPA compliant.",
      robots: "index,follow",
      canonicalPath: "/privacy",
      ogType: "website",
    };
  }

  if (pathname === "/terms") {
    return {
      title: "Terms of Service - Double O",
      description:
        "Read the Terms of Service for the Double O secrets management platform.",
      robots: "index,follow",
      canonicalPath: "/terms",
      ogType: "website",
    };
  }

  if (pathname.startsWith("/blog/")) {
    const slug = pathname.replace("/blog/", "");
    const post = getBlogPostBySlug(slug);

    if (post) {
      return {
        title: `${post.title} - Double O Blog`,
        description: post.description,
        robots: "index,follow",
        canonicalPath: `/blog/${post.slug}`,
        ogType: "article",
        structuredData: {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.description,
          datePublished: post.publishedAt,
          dateModified: post.publishedAt,
          url: absoluteUrl(`/blog/${post.slug}`),
          publisher: {
            "@type": "Organization",
            name: "Double O",
            url: absoluteUrl("/"),
          },
        },
      };
    }
  }

  if (pathname === "/auth/renew") {
    return {
      title: "Renew Token - Double O",
      description: "Generate a new access token for your Double O secret.",
      robots: "noindex,nofollow",
      ogType: "website",
    };
  }

  return {
    title: "Double O App",
    description: "Secure secrets management dashboard.",
    robots: "noindex,nofollow",
    ogType: "website",
  };
}

export function applySeo(pathname: string) {
  if (typeof document === "undefined") return;

  cleanupManagedSeo();

  const payload = getSeoPayload(pathname);
  const canonicalUrl = payload.canonicalPath
    ? absoluteUrl(payload.canonicalPath)
    : undefined;
  const ogImage = absoluteUrl(DEFAULT_OG_IMAGE_PATH);

  document.title = payload.title;

  upsertMeta("description", payload.description);
  upsertMeta("robots", payload.robots);
  upsertMeta("og:title", payload.title);
  upsertMeta("og:description", payload.description);
  upsertMeta("og:type", payload.ogType ?? "website");
  upsertMeta("og:url", canonicalUrl ?? absoluteUrl(pathname));
  upsertMeta("og:image", ogImage);
  upsertMeta("twitter:card", "summary_large_image");
  upsertMeta("twitter:title", payload.title);
  upsertMeta("twitter:description", payload.description);
  upsertMeta("twitter:image", ogImage);
  upsertCanonical(canonicalUrl);
  upsertStructuredData(payload.structuredData);
}
